/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React, { useState } from 'react'
import { paragraphSmall, weightBold, weightRegular } from 'src/components/css'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import {
  JERSEY_EDIT_TOOLTIP,
  JERSEY_LOCKED_SUSPENDED_TOOLTIP,
  JERSEY_LOCKED_WAIVER_TOOLTIP,
  getJerseyConflictTooltip,
} from '../utils/statusCopy'

// Shown when the player has no number yet, so the cell is still clickable.
const EMPTY_NUMBER_PLACEHOLDER = '—'

export interface AttendanceJerseyCellProps {
  playerId: string
  playerName: string
  playerNumber?: number | string
  isConflict: boolean
  // Other players sharing this number, named in the conflict tooltip.
  conflictNames: string[]
  isSuspended: boolean
  isWaiverLocked: boolean
  onUpdatePlayerNumber: (playerId: string, playerNumber: number) => void
}

/**
 * Jersey number cell: a filled number box that reads as an editable field and
 * swaps to an inline input on click. Enter / blur save, Escape cancels.
 */
const AttendanceJerseyCell: React.FC<AttendanceJerseyCellProps> = ({
  playerId,
  playerName,
  playerNumber,
  isConflict,
  conflictNames,
  isSuspended,
  isWaiverLocked,
  onUpdatePlayerNumber,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedNumber, setEditedNumber] = useState('')

  const isLocked = isWaiverLocked || isSuspended
  const currentNumber = playerNumber == null ? '' : String(playerNumber)
  const hasNumber = currentNumber !== ''

  const tooltip = isWaiverLocked
    ? JERSEY_LOCKED_WAIVER_TOOLTIP
    : isSuspended
    ? JERSEY_LOCKED_SUSPENDED_TOOLTIP
    : isConflict
    ? getJerseyConflictTooltip(conflictNames)
    : JERSEY_EDIT_TOOLTIP

  const startEditing = () => {
    if (isLocked) {
      return
    }
    setEditedNumber(currentNumber)
    setIsEditing(true)
  }

  const stopEditing = (save: boolean) => {
    if (save && editedNumber !== '' && editedNumber !== currentNumber) {
      onUpdatePlayerNumber(playerId, Number(editedNumber))
    }
    setIsEditing(false)
    setEditedNumber('')
  }

  if (isEditing && !isLocked) {
    return (
      <input
        css={inputStyle}
        type='number'
        maxLength={3}
        value={editedNumber}
        aria-label={`Jersey number for ${playerName}`}
        autoFocus
        onInput={(e) => {
          if (e.currentTarget.value.length > 3) {
            e.currentTarget.value = e.currentTarget.value.slice(0, 3)
          }
        }}
        onChange={(e) => setEditedNumber(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            stopEditing(true)
          }
          if (e.key === 'Escape') {
            // Keep Escape from also closing the modal.
            e.stopPropagation()
            stopEditing(false)
          }
        }}
        onBlur={() => stopEditing(true)}
      />
    )
  }

  return (
    <Tooltip title={tooltip} zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}>
      <button
        type='button'
        css={boxStyle(isConflict, isLocked)}
        aria-disabled={isLocked}
        aria-label={isLocked ? tooltip : `Edit jersey number for ${playerName}`}
        onClick={startEditing}
      >
        {hasNumber ? (
          currentNumber
        ) : (
          <span css={placeholderStyle}>{EMPTY_NUMBER_PLACEHOLDER}</span>
        )}
      </button>
    </Tooltip>
  )
}

export default AttendanceJerseyCell

const boxStyle = (isConflict: boolean, isLocked: boolean) => css`
  ${paragraphSmall}
  ${isConflict ? weightBold : weightRegular}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 32px;
  padding: 0 4px;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
  color: ${isConflict ? colors.ERROR_300 : colors.NEUTRAL_0};
  background-color: ${isLocked ? 'transparent' : colors.SOFT_STEEL};
  border: 1px ${isLocked ? 'dashed' : 'solid'}
    ${isConflict ? colors.ERROR_200 : colors.SECONDARY_100};
  cursor: ${isLocked ? 'not-allowed' : 'pointer'};
  transition: border-color 0.15s ease;

  ${!isLocked &&
  css`
    &:hover {
      border-color: ${isConflict ? colors.ERROR_100 : colors.NEUTRAL_100};
    }
  `}

  &:focus-visible {
    outline: 2px solid ${colors.NEUTRAL_50};
    outline-offset: 2px;
  }
`
const placeholderStyle = css`
  color: ${colors.NEUTRAL_100};
`
const inputStyle = css`
  ${paragraphSmall}
  width: 48px;
  height: 32px;
  text-align: center;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.NEUTRAL_0};
  color: ${colors.SECONDARY_300};
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`
